import React from 'react';
import Link from 'next/link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import useStyles from './styles';
import SubmissionStatus from 'atoms/SubmissionStatus';
import Typography from '@mui/material/Typography';
import { Submission } from 'types/submissions';
import { ROUTES } from 'config/Nav';
import { Media, formatDate } from '@elevator/ui';
import clsx from 'clsx';
import Image from 'next/image';

/**
 * Props for the SubmissionCard component.
 */
export interface SubmissionCardProps {
  /** The submission data to display in the card. */
  submission: Submission;
}

/**
 * SubmissionCard Component
 *
 * Displays a clickable card showing submission information including image,
 * title, date, service type, add-ons, and status indicator. Clicking the
 * card navigates to the submission detail page.
 *
 * Used in:
 * - MySubmissions layout (displays user's submissions grouped by status)
 */
const SubmissionCard = ({ submission }: SubmissionCardProps) => {
  const { classes } = useStyles();

  const {
    contentTitle,
    service,
    contentImage,
    status,
    createdAt,
    flagged,
    orderNumber,
    services = [],
    order,
  } = submission;

  const statusLabel = flagged ? 'Flagged' : status;
  const submissionUrl = `${ROUTES.ACCOUNT.SUBMISSIONS.to}/${orderNumber}`;

  // Filter add-ons (services with a parent)
  const addOns = services.filter((serviceItem) => serviceItem.parent);
  const addOnNames =
    addOns.length > 0 ? addOns.map((addOn) => addOn.name).join(', ') : '';

  // Determine if contentImage is a Media object with sourceUrl
  const imageSourceUrl =
    contentImage && typeof contentImage === 'object' && 'sourceUrl' in contentImage
      ? (contentImage as Media).sourceUrl
      : null;

  return (
    <Paper
      component={Link}
      href={submissionUrl}
      data-component="SubmissionCard"
      className={classes.root}
      elevation={1}
      square
      aria-label={`View submission: ${contentTitle}`}
    >
      <Stack direction="row" alignItems="center" className={classes.leftContainer}>
        {/* Status bar */}
        <Box
          className={clsx(classes.statusColor, {
            [classes.colorPending]:
              statusLabel === 'Approval' || statusLabel === 'Review',
            [classes.colorApproved]:
              statusLabel === 'Completed' || statusLabel === 'Approved',
            [classes.colorRejected]: statusLabel === 'Rejected',
            [classes.colorFlagged]: statusLabel === 'Flagged',
          })}
        />
        {/* Submission Image */}
        {imageSourceUrl && (
          <Box className={classes.imageContainer}>
            <Image
              src={imageSourceUrl}
              className={classes.attachedImage}
              alt={`Submission image for ${contentTitle}`}
              width={80}
              height={80}
            />
          </Box>
        )}
        {/* Text content fills available space */}
        <Stack
          direction="column"
          justifyContent="center"
          className={classes.textContainer}
        >
          <Typography variant="overline2" className={classes.dateAndType}>
            {formatDate(parseInt(createdAt), {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <Typography variant="body2" className={classes.submissionTitle}>
            {contentTitle}
          </Typography>
          <Typography variant="overline2" className={classes.dateAndType}>
            {service?.name}
            {addOnNames && `  + ${addOnNames}`}
          </Typography>
        </Stack>
      </Stack>
      {/* Right-aligned status indicator */}
      <SubmissionStatus
        variant={statusLabel}
        orderStatus={order.status}
        orderFlagged={order.flagged}
      />
    </Paper>
  );
};

export default SubmissionCard;
